/**
 * @fileoverview B2B event create and edit page.
 * @module pages/B2BEventForm
 */

import { Backdrop, Button, CircularProgress } from "@mui/material";
import LanguageSelector from "../components/LanguageSelector/index.jsx";
import NotificationBell from "../components/NotificationBell/index.jsx";
import B2BEventParticipatingCompanyView from "../components/B2BEventParticipatingCompanyView/index.jsx";
import ConfirmationModal from "../modals/ConfirmationModal/index.jsx";
import B2BEventParticipatingCompanyModal from "../modals/B2BEventParticipatingCompanyModal/index.jsx";
import { ReactComponent as SaveIcon } from "../assets/icons/CompanyEditPage/SaveIcon.svg";
import { ReactComponent as TitleIcon } from "../assets/icons/EventCreation/TitleIcon.svg";
import { ReactComponent as ChevronLeftIcon } from "../assets/icons/EventCreation/ChevronLeftIcon.svg";
import { ReactComponent as CheckCircleIcon } from "../assets/icons/BusinessEvents/CheckCircleIcon.svg";
import styles from "../styles/B2BEventForm.module.css";
import { useB2BEventForm } from "./b2bEventForm/useB2BEventForm";
import B2BEventBannerSection from "./b2bEventForm/B2BEventBannerSection";
import B2BEventDetailsSection from "./b2bEventForm/B2BEventDetailsSection";
import B2BEventParticipantsSection from "./b2bEventForm/B2BEventParticipantsSection";

const BACKDROP_SPINNER_SIZE = 60;
const BACKDROP_SPINNER_THICKNESS = 4;
const SAVE_SPINNER_SIZE = 24;
const DRAWER_LAYER_OFFSET = 1;

/**
 * Full-page add/edit used on tablet and mobile.
 * @param {Object} props
 * @returns {JSX.Element}
 */
const ParticipatingCompanyPage = ({ form }) => (
  <B2BEventParticipatingCompanyView
    navigate={form.navigate}
    eventId={form.eventData?._id}
    eventDateStart={form.eventDateStart}
    eventDateEnd={form.eventDateEnd}
    eventTimeStart={form.formData.horaInicio || null}
    eventTimeEnd={form.formData.horaFin || null}
    participatingCompanies={form.participatingCompanies}
    buyerCompanies={form.buyerCompanies}
    selectedParticipant={form.selectedParticipant}
    error={form.participantError}
    onBack={form.closeParticipantFlow}
    onCancel={form.closeParticipantFlow}
    onSuccess={form.handleParticipantFlowSuccess}
  />
);

/**
 * @param {Object} props
 * @returns {JSX.Element}
 */
const FormHeader = ({ t, navigate, onBack }) => (
  <div className={styles.header}>
    <div className={styles.languages}>
      <NotificationBell navigate={navigate} />
      <LanguageSelector />
    </div>
    <div className={styles.backNavigation}>
      <button className={styles.backButton} onClick={onBack}>
        <ChevronLeftIcon />
        <span>{t("common.goBack")}</span>
      </button>
    </div>
    <div className={styles.headerInfoContainer}>
      <div className={styles.headerInfo}>
        <TitleIcon className={styles.titleIcon} />
        <h1 className={styles.title}>{t("pages.b2bEvents.formHeader")}</h1>
      </div>
    </div>
  </div>
);

/**
 * @param {Object} props
 * @returns {JSX.Element}
 */
const ParticipantDialogs = ({ form }) => (
  <>
    {form.addParticipantModalOpen && (
      <B2BEventParticipatingCompanyModal
        open={form.addParticipantModalOpen}
        eventId={form.eventData?._id}
        eventDateStart={form.eventDateStart}
        eventDateEnd={form.eventDateEnd}
        eventTimeStart={form.formData.horaInicio || null}
        eventTimeEnd={form.formData.horaFin || null}
        participatingCompanies={form.participatingCompanies}
        buyerCompanies={form.buyerCompanies}
        selectedParticipant={form.selectedParticipant}
        error={form.participantError}
        onCancel={form.closeParticipantFlow}
        onSuccess={form.handleParticipantFlowSuccess}
      />
    )}
    {form.deleteParticipantModalOpen && (
      <ConfirmationModal
        dialogRef={form.deleteParticipantDialogRef}
        open={form.deleteParticipantModalOpen}
        onClose={form.handleCloseDeleteParticipantModal}
        onConfirm={form.handleConfirmDeleteParticipant}
        title={form.t("components.confirmationModal.title")}
        message={form.t("pages.b2bEventForm.deleteParticipatingCompanyWarning")}
        confirmButtonText={form.t("components.confirmationModal.confirm")}
        cancelButtonText={form.t("components.confirmationModal.cancel")}
        isLoading={form.isDeletingParticipant}
      />
    )}
  </>
);

/**
 * @param {Object} props
 * @returns {JSX.Element}
 */
const EventFormBody = ({ form }) => (
  <>
    <FormHeader t={form.t} navigate={form.navigate} onBack={form.handleGoBack} />
    <div className={styles.formContainer}>
      <form onSubmit={form.handleSubmit} className={styles.eventForm}>
        <div className={styles.formGroups}>
          <div className={styles.formGroupOne}>
            <B2BEventBannerSection
              t={form.t}
              formData={form.formData}
              isEditMode={form.isEditMode}
              error={form.error}
              imageRef={form.imageRef}
              onDragEnter={form.onDragEnter}
              onDragLeave={form.onDragLeave}
              onDrop={form.onDrop}
              onImageChange={form.handleImageChange}
              onAudienciaChange={form.handleAudienciaCheckboxChange}
              onActivoChange={form.handleActivoChange}
            />
            <B2BEventDetailsSection
              t={form.t}
              formData={form.formData}
              showError={form.showError}
              nameValidation={form.nameValidation}
              onChange={form.handleInputChange}
            />
          </div>
          <B2BEventParticipantsSection
            t={form.t}
            eventData={form.eventData}
            participatingCompanies={form.participatingCompanies}
            searchParticipating={form.searchParticipating}
            searchBarWidth={form.participatingCompaniesSearchBarWidth}
            filteredParticipatingCompanies={form.filteredParticipatingCompanies}
            participantError={form.participantError}
            showDropdown={form.showDropdown}
            dropdownRef={form.dropdownRef}
            onSearch={form.handleSearchParticipating}
            onAdd={form.openAddParticipant}
            onToggleDropdown={form.toggleDropdown}
            onEdit={form.editParticipant}
            onDelete={form.handleOpenDeleteParticipantModal}
          />
        </div>
        <div className={styles.saveSectionContainer}>
          <div className={styles.saveSectionContent}>
            <div className={styles.messageContainer}>
              {form.t("pages.b2bEventForm.publishInstructions")}
            </div>
            <Button
              type="submit"
              variant="contained"
              className={styles.saveButton}
              disabled={form.saveDisabled}
              startIcon={form.loading ? null : <SaveIcon />}
            >
              {form.loading ? (
                <CircularProgress size={SAVE_SPINNER_SIZE} color="inherit" />
              ) : (
                form.t("pages.b2bEventForm.publishEvent")
              )}
            </Button>
          </div>
        </div>
      </form>
      <ParticipantDialogs form={form} />
    </div>
  </>
);

/**
 * Create or edit a B2B event. Access stays limited to staff who can manage the event.
 * @returns {JSX.Element|null}
 */
const B2BEventForm = () => {
  const form = useB2BEventForm();

  if (!form.allowed) {
    return null;
  }

  return (
    <div className={styles.container}>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + DRAWER_LAYER_OFFSET,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        }}
        open={form.isEditMode && form.loading}
      >
        <CircularProgress
          size={BACKDROP_SPINNER_SIZE}
          thickness={BACKDROP_SPINNER_THICKNESS}
          sx={{ color: "#4eabe7" }}
        />
      </Backdrop>
      {form.showParticipatingCompanyView ? (
        <ParticipatingCompanyPage form={form} />
      ) : (
        <EventFormBody form={form} />
      )}
      {form.showSuccessMessage && (
        <div
          className={`${styles.successMessage} ${form.isSlidingUp ? styles.slideUp : ""}`}
        >
          <CheckCircleIcon className={styles.successMessageIcon} />
          {form.t("pages.b2bEvents.creationMessage")}
        </div>
      )}
    </div>
  );
};

export default B2BEventForm;
